import { useEffect, useLayoutEffect, useState, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import type { TvShow } from "../../../services/apiTypes"
import styles from '../../../App.module.css'
import useShows from '../hooks/useShows'
import { getReleaseCountdown, parseReleaseDate } from '../../show/utils/show'
import FilterButton from '../../../components/common/FilterButton'
import { useAppContext } from '../../../state/AppContext'
import useGenreFilter from '../../../hooks/useGenreFilter'
import GenreSelect from '../../../components/common/GenreSelect'
import LibraryStatusBanner from '../../../components/common/LibraryStatusBanner'

type UpcomingFilter = 'continue' | 'all'

const upcomingFilters: Array<{ key: UpcomingFilter; label: string; tooltip: string }> = [
    { key: 'continue', label: 'Continue', tooltip: 'Upcoming episodes only for shows you\'ve already started watching at least 1 episode.' },
    { key: 'all', label: 'All', tooltip: 'All tracked shows with a future episode date, including ones you haven\'t started yet.' },
]

function UpcomingCard({ show, onClick }: { show: TvShow; onClick: (show: TvShow) => void }) {
    const episode = show.nextAiringEpisode
    const countdown = getReleaseCountdown(show)
    const isToday = countdown === 'Today'

    const currentYear = new Date().getFullYear();
    const airDate = parseReleaseDate(episode!.releaseDate);

    const displayReleaseDate = airDate.getFullYear() === currentYear
        ? airDate.toLocaleDateString('en-UK', { weekday: 'short', month: 'short', day: 'numeric' }) // "Thu, Jun 25"
        : airDate.toLocaleDateString('en-UK', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); // "Sun, Jan 3, 2027"

    return (
        <Link
            to={`/show/${encodeURIComponent(show.id)}`}
            className={styles.upcomingCard}
            onClick={(e) => {
                if(!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
                    e.preventDefault();
                    onClick(show);
                }
            }}
        >
            <div className={styles.upcomingCardPoster}>
                {show.posterUrl
                    ? <img src={show.posterUrl} alt={show.title} className={styles.showCardImage} />
                    : <div className={styles.upcomingPosterPlaceholder} />}
            </div>
            <div className={styles.upcomingCardInfo}>
                <strong className={styles.upcomingTitle}>{show.title}</strong>
                <span>S{episode?.season} E{episode?.episode} · {episode?.title}</span>
                {displayReleaseDate && (
                    <span className={styles.upcomingNetwork}>
                        {displayReleaseDate}
                    </span>
                )}
            </div>
            <div className={`${styles.upcomingCountdown} ${isToday ? styles.upcomingCountdownToday : ''}`}>
                {isToday ? '🟣 Today' : countdown}
            </div>
        </Link>
    )
}

export default function UpcomingPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { shows, loading, error, retry } = useShows('upcoming')
    const { settings } = useAppContext()
    const viewMode = settings?.defaultUpcomingView ?? 'list'
    const [genre, setGenre] = useState(() => sessionStorage.getItem('upcoming_genre') ?? '')
    useEffect(() => {
        sessionStorage.setItem('upcoming_genre', genre)
    }, [genre])

    const filter = (searchParams.get('filter') as UpcomingFilter | null) ?? 'continue'

    const filteredShows = useMemo(() => {
        if(filter === 'continue') {
            return shows.filter((s) => (s.episodesWatched ?? 0) > 0)
        }
        return shows
    }, [shows, filter])
    const { genres, counts, loading: genresLoading, filter: filterByGenre } = useGenreFilter(filteredShows)
    const visibleShows = filterByGenre('', genre)

    useLayoutEffect(() => {
        if(loading || (genre && genresLoading)) {
            return
        }

        const savedScroll = sessionStorage.getItem('upcoming_scroll')
        if(savedScroll === null) {
            return
        }

        sessionStorage.removeItem('upcoming_scroll')
        const scrollTop = Number(savedScroll)
        if(Number.isFinite(scrollTop)) {
            window.scrollTo({ top: scrollTop, behavior: 'instant' as ScrollBehavior })
        }
    }, [loading, genresLoading, visibleShows, genre])

    function openShow(show: TvShow) {
        sessionStorage.setItem('upcoming_scroll', String(window.scrollY))
        navigate(`/show/${encodeURIComponent(show.id)}`)
    }

    const calendarGroups = useMemo(() => {
        const groups: Record<string, TvShow[]> = {}
        for(const show of visibleShows) {
            const episode = show.nextAiringEpisode
            if(!episode?.releaseDate) continue
            const date = parseReleaseDate(episode.releaseDate)
            const today = new Date()
            const todayKey = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
            const dateKeyValue = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
            const dayOffset = Math.round((dateKeyValue - todayKey) / (1000 * 60 * 60 * 24))
            const dateKey = dayOffset === 0
                ? 'Today'
                : dayOffset === 1
                    ? 'Tomorrow'
                    : date.toLocaleDateString('en-UK', { weekday: 'long', month: 'long', day: 'numeric' })
            if(!groups[dateKey]) groups[dateKey] = []
            groups[dateKey].push(show)
        }
        return Object.entries(groups)
    }, [visibleShows])

    function setFilter(next: UpcomingFilter) {
        const params = new URLSearchParams(searchParams)
        if(next === 'continue') {
            params.delete('filter')
        } else {
            params.set('filter', next)
        }
        setSearchParams(params)
    }

    if(loading) {
        return <main className={styles.mainPanel}><LibraryStatusBanner /><div className={styles.loadingBar} /></main>
    }
    if(error) {
        return <main className={styles.mainPanel}><LibraryStatusBanner /><div className={styles.emptyState} role="alert"><p>Could not load shows: {error}</p><button className={styles.secondaryButton} type="button" onClick={() => void retry()}>Try again</button></div></main>
    }

    return (
        <main className={styles.mainPanel}>
            <LibraryStatusBanner />
            <section className={styles.tabContent}>
                <div className={styles.watchingToolbar}>
                    <div className={styles.sortControls}>
                        {upcomingFilters.map((f) => (
                            <FilterButton
                                key={f.key}
                                label={f.label}
                                active={filter === f.key}
                                tooltip={f.tooltip}
                                onClick={() => setFilter(f.key)}
                            />
                        ))}
                        <GenreSelect genres={genres} counts={counts} loading={genresLoading} value={genre} onChange={setGenre} />
                    </div>
                </div>

                {genre && genresLoading && visibleShows.length === 0 ? (
                    <div className={styles.loadingBar} />
                ) : visibleShows.length === 0 ? (
                    <div className={styles.emptyState}>
                        {filter === 'continue'
                            ? 'No upcoming episodes for shows you\'re watching. Switch to All to see everything.'
                            : 'No upcoming releases yet. Add shows to track future episodes.'}
                    </div>
                ) : viewMode === 'list' ? (
                    <div className={styles.upcomingGrid}>
                        {visibleShows.map((show) => (
                            <UpcomingCard
                                key={show.id}
                                show={show}
                                onClick={openShow}
                            />
                        ))}
                    </div>
                ) : (
                    <div className={styles.calendarContainer}>
                        {calendarGroups.map(([dateTitle, dateShows]) => (
                            <div key={dateTitle} className={styles.calendarDateGroup}>
                                <div className={styles.calendarDateHeader}>
                                    <span>🗓️ {dateTitle}</span>
                                    <span className={styles.calendarCountBadge}>{dateShows.length} release{dateShows.length > 1 ? 's' : ''}</span>
                                </div>
                                <div className={styles.upcomingGrid}>
                                    {dateShows.map((show) => (
                                        <UpcomingCard
                                            key={show.id}
                                            show={show}
                                            onClick={openShow}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </main>
    )
}
